import React from "react";
import "./dshashsetscreen.css"
import HashSetInsert1 from "./assets/insert/one/HashSetInsert1";
import HashSetInsert9 from "./assets/insert/nine/HashSetInsert9";
import HashSetInsert17 from "./assets/insert/seventeen/HashSetInsert17";
import HashSetInsert85 from "./assets/insert/eightyfive/HashSetInsert85";
import HashSetInsert87 from "./assets/insert/eightyseven/HashSetInsert87";
import HashSetInsert87Two from "./assets/insert/eightyseventwo/HashSetInsert87Two";
import HashSetInsertNeg96 from "./assets/insert/negninetysix/HashSetInsertNeg96";
import HashSetImplementationArrayArray from "./assets/implementation/HashSetImplementationArrayArray";
import HashSetImplementationArrayLinkedList from "./assets/implementation/HashSetImplementationArrayLinkedList";
import HashSetImplementationFlatArray from "./assets/implementation/HashSetImplementationFlatArray";
import HashSetInsertUnhappyPath from "./assets/insert/HashSetInsertUnhappyPath";
import HashSetRemoveUnhappyPath from "./assets/remove/HashSetRemoveUnhappyPath";
import HashSetRemoveNeg96 from "./assets/remove/negninetysix/HashSetRemoveNeg96";
import HashSetRemoveNeg7 from "./assets/remove/negseven/HashSetRemoveNeg7";
import HashSetRemoveNeg44 from "./assets/remove/negfortyfour/HashSetRemoveNeg44";
import HashSetRemove82 from "./assets/remove/eightytwo/HashSetRemove82";
import HashSetRemove21 from "./assets/remove/twentyone/HashSetRemove21";
import {Paths} from "../../app/components/sidebar/Paths";
import LinkComponent, {ALinkTarget} from "../../app/components/link/LinkComponent";
import {CalloutComponent} from "../../app/components/callout/CalloutComponent";
import {
    AccordionHeaderTextEmphasized,
    AccordionHeaderTextExtraTimeComplexity,
    AccordionHeaderTextTimeComplexityComponent
} from "../../app/components/accordion/AccordionHeaderTextComponent";
import {AccordionComponent} from "../../app/components/accordion/AccordionComponent";
import {AccordionItemComponent} from "../../app/components/accordion/AccordionItemComponent";
import {CarouselItemComponent} from "../../app/components/carousel/CarouselItemComponent";
import {CarouselComponent} from "../../app/components/carousel/CarouselComponent";
import {CodeComponent} from "../../app/components/code/CodeComponent";
import {HashSetInsert85FullCaption} from "./assets/insert/eightyfive/HashSetInsert85Steps";
import {HashSetInsert1FullCaption} from "./assets/insert/one/HashSetInsert1Steps";
import {HashSetInsert17FullCaption} from "./assets/insert/seventeen/HashSetInsert17Steps";
import {HashSetInsert87FullCaption} from "./assets/insert/eightyseven/HashSetInsert87Steps";
import {HashSetInsert87TwoFullCaption} from "./assets/insert/eightyseventwo/HashSetInsert87TwoSteps";
import {HashSetInsert9FullCaption} from "./assets/insert/nine/HashSetInsert9Steps";
import {HashSetInsertNeg96FullCaption} from "./assets/insert/negninetysix/HashSetInsertNeg96Steps";
import {HashSetInsertUnhappyFullCaption} from "./assets/insert/HashSetInsertUnhappySteps";
import {HashSetRemoveUnhappyFullCaption} from "./assets/remove/HashSetRemoveUnhappySteps";
import {HashSetRemoveNeg96FullCaption} from "./assets/remove/negninetysix/HashSetRemoveNeg96Steps";
import {HashSetRemoveNeg44FullCaption} from "./assets/remove/negfortyfour/HashSetRemoveNeg44Steps";
import {HashSetRemoveNeg7FullCaption} from "./assets/remove/negseven/HashSetRemoveNeg7Steps";
import {HashSetRemove82FullCaption} from "./assets/remove/eightytwo/HashSetRemove82Steps";
import {HashSetRemove21FullCaption} from "./assets/remove/twentyone/HashSetRemove21Steps";
import {H2SectionComponent, H3SectionComponent} from "../../app/components/section/SectionComponent";
import {ProblemSetComponent} from "../components/problemset/ProblemSet";
import {AlertComponent} from "../../app/components/alert/AlertComponent";
import {GradientHeaderComponent} from "../../app/components/gradientheader/GradientHeaderComponent";
import {ScreenComponent} from "../../app/components/screen/ScreenComponent";
import {ThemeColor} from "../../home/components/homecard/HomeCardComponent";

export default function DSHashSetScreen() {
    return (<ScreenComponent>
        <Header/>
        <Conceptualizing/>
        <Visualizing/>
        <TimeComplexity/>
        <ProblemSet/>
    </ScreenComponent>)
}

function Header() {
    return (
        <GradientHeaderComponent title={Paths.dataStructures.nested.hashset.name}
                                 tagline={Paths.dataStructures.nested.hashset.tagline}/>
    )
}

function Conceptualizing() {
    return (<H2SectionComponent header="Conceptualizing HashSets" removeTopPadding={true}>
        <ul>
            <li>A HashSet is the Crayola Crayon box of computer science.</li>
            <li>When you open up a new box of crayons, you have a bunch of different colors, in fact you have no
                duplicates. You don't ever have two red crayons, and if your friend offered you a red crayon because
                yours broke, you wouldn't keep both, you would remove the broken one and put the new one there
                instead. There are no duplicates in sets.
            </li>
            <li>On top of that, there is no set spot for each crayon. You pick up the red, and you put it back down
                where the blue crayon is because that's the next one you pick up. There is no specific order to
                sets.
            </li>
        </ul>
    </H2SectionComponent>)
}

function Visualizing() {
    return (<H2SectionComponent header="Visualizing HashSets">
        <AlertComponent color={ThemeColor.Secondary}>
            <p><strong>NOTE:</strong> A HashSet is an <i>abstract</i> data structure. Unlike an Integer, which
                just <i>is</i> a 32-bit number, HashSets are really a structure with defined principles that can be
                implemented in many ways.</p>
        </AlertComponent>
        <Principles/>
        <Implementation/>
    </H2SectionComponent>)
}

function Principles() {
    const getHashFunction = "fun getHash(s: String): Int { return s.length }"
    return (<H3SectionComponent header="Principles of a HashSet" removeTopPadding={true}>
        <ul>
            <li><strong><CodeComponent className="codeValue">VALUES</CodeComponent> are unique, <CodeComponent
                className="codeHash">HASHES</CodeComponent> may not be.</strong> Take for example, you have the
                following function: <CodeComponent>{getHashFunction}</CodeComponent> In this case, the words "cat" and
                "dog" can both appear in the set, as they are unique <CodeComponent
                    className="codeValue">VALUES</CodeComponent>, however, they share the same <CodeComponent
                    className="codeHash">HASH</CodeComponent> which is 3. If we tried adding a duplicate <CodeComponent
                    className="codeValue">VALUE</CodeComponent>, it will overwrite the existing entry of the
                same <CodeComponent className="codeValue">VALUE</CodeComponent>, or, it just won't be added. Take, for
                example, if we tried adding "cat" again, we would see "cat" has already been added, and would just
                ignore the addition of the second "cat" <CodeComponent className="codeValue">VALUE</CodeComponent>.
            </li>
            <li><strong>There is no specific order to a set.</strong> Instead of an ordered-index like we saw in Arrays,
                we use something called a <CodeComponent className="codeHash">HASH</CodeComponent> to lookup
                our <CodeComponent className="codeValue">VALUE</CodeComponent> in the set. There is no limit to what
                the <CodeComponent className="codeHash">HASH</CodeComponent> key can be, though typically you will see
                it be a number of some kind. It really just depends on how the HashSet is implemented under the hood.
            </li>
            <li><strong>Ideally, all <CodeComponent className="codeValue">VALUES</CodeComponent> in a HashSet would have
                a unique <CodeComponent className="codeHash">HASH</CodeComponent>.</strong> When two or
                more <CodeComponent className="codeValue">VALUES</CodeComponent> have the same <CodeComponent
                    className="codeHash">HASH</CodeComponent>, it's called a collision. The more collisions a set has,
                the worse it will perform.
            </li>
        </ul>
    </H3SectionComponent>)
}

function Implementation() {
    const arrayArrayType = "Array<Array<String>>"
    const arrayLinkedListType = "Array<LinkedList<String>>"
    return (<H3SectionComponent header="Implementation of a HashSet">
        <ul>
            <li>There are several ways to implement a HashSet. I will not cover all of them, but I will show a few
                examples just to highlight some differences. The exact syntax isn't as important as the foundational
                principles covered in the previous section.
            </li>
            <li>Let's look at the example above again. We have "cat" and "dog" that both collide at 3. Let's think about
                how we would implement the HashSet under the hood. Perhaps you are already picturing an Array of some
                kind. Let's say we have an Array of size 8 to begin with. In our case, we add "cat", and it inserts
                "cat" into our array at index 3. Now, we want to add "dog". We figure out the <CodeComponent
                    className="codeHash">HASH</CodeComponent> is 3, we go to input into our array at index 3 and oh no,
                we already have an element there, "cat". What do we do? Collisions can be handled in multiple ways.
                Again, HashSets are not a set thing, you can implement them in many different ways. There is no right or
                wrong way here. Some are slightly better than others, but all have their pros and cons. The best
                strategy is to just avoid as many collisions as you can, while always be preparing to handle them. Let's
                go over a few implementation options.
            </li>
            <ul>
                <li>Some may handle this by having the HashSet be an <CodeComponent>{arrayArrayType}</CodeComponent>.
                    With a 2D-array, you can just keep adding all strings of the same length to the same sub-array.
                </li>
                <HashSetImplementationArrayArray/>
                <li>Some may handle this by having the HashSet be
                    an <CodeComponent>{arrayLinkedListType}</CodeComponent>. Where a "cat" node would point to a "dog"
                    node, etc.
                </li>
                <HashSetImplementationArrayLinkedList/>
                <li>Others may not want sub-collections, but would prefer everything to live in the main array. So,
                    perhaps they handle collisions by moving it to the next index forward. "cat" is already at index 3,
                    so we will put dog in index 4, instead.
                </li>
                <HashSetImplementationFlatArray/>
            </ul>
            <li>At this point, you may be thinking, "Wait a minute, if all <CodeComponent
                className="codeValue">VALUES</CodeComponent> are unique, and it's best to have unique <CodeComponent
                className="codeHash">HASHES</CodeComponent>, why don't we just use the <CodeComponent
                className="codeValue">VALUE</CodeComponent> as the <CodeComponent
                className="codeHash">HASH</CodeComponent>?" Honestly, it's a pretty interesting question, but it all
                comes back to the idea that HashSets are an <i>abstract</i> data structure.
            </li>
            <ul>
                <li>Let's say we are using that Array to implement our HashSet. For ease, let's assume
                    our <CodeComponent className="codeValue">VALUES</CodeComponent> are integers.
                </li>
                <ul>
                    <li>Let's add 3 to our Array. Easy, <CodeComponent>array[3] = 3</CodeComponent></li>
                    <li>Now, let's add 1,000,000,000,000. <CodeComponent>array[1,000,000,000,000] =
                        [1,000,000,000,000]</CodeComponent></li>
                    <li>Finally, let's add -12. <CodeComponent>array[-12] = -12</CodeComponent></li>
                </ul>
                <li>Wait a minute. Maybe some of you have already spotted the issue, but let's go over some.</li>
                <ul>
                    <li>First, you cannot have a negative index on an array. If you tried doing <CodeComponent>array[-12]
                        = -12</CodeComponent> your program would crash.
                    </li>
                    <li>Secondly, let's assume you knew with confidence your <CodeComponent
                        className="codeHash">HASHES</CodeComponent> would be positive, as is the case in our length
                        function above. Even in that case, your indexes could vary widely. In our example above, our
                        array would be at least size 1,000,000,000,001. But you only have 3 elements in it. Space
                        complexity is just as important as time. You would not want to allocate all of that memory for
                        an array that is only holding 3 elements.
                    </li>
                    <li>And while it may not be super apparent here, with our <CodeComponent
                        className="codeValue">VALUES</CodeComponent> being integers, there are also more obvious
                        examples of why using the <CodeComponent className="codeValue">VALUE</CodeComponent> for
                        our <CodeComponent className="codeHash">HASH</CodeComponent> is a bad idea. For example, what if
                        we aren't storing numbers, but instead Strings, or Objects?
                    </li>
                </ul>
            </ul>
            <li>There is a bunch of research done on how best to implement a HashSet. It's quite heavy to get into here,
                and it doesn't matter too much in interviews to be honest, as you will mostly be using the built-in
                syntax used by whatever your preferred language is. Just know, <i>generally</i>, you never want your
                array (or whatever memory you are setting aside) to be more than double the size of the set itself.
            </li>
        </ul>
    </H3SectionComponent>)
}

function TimeComplexity() {
    const arrayArrayType = "Array<Array<String>>"
    return (<H2SectionComponent header="What is the time complexity?">
        <CalloutComponent color={ThemeColor.Secondary}>
            <strong>Things to keep in mind...</strong>
            <ul>
                <li>For all examples, I will be using the idea of implementing our HashSet with
                    an <CodeComponent>{arrayArrayType}</CodeComponent>. This is solely do to the fact that
                    I've <LinkComponent link={Paths.dataStructures.nested.array.path} target={ALinkTarget.internal}>already
                        covered Arrays</LinkComponent> in a different post, and reviewed time complexities there.
                </li>
                <li><strong>GenerateHash:</strong> <CodeComponent>O(1)</CodeComponent></li>
                <ul>
                    <li><CodeComponent className="codeHash">HASH</CodeComponent> functions should always run in constant
                        time. While you can technically implement a <CodeComponent
                            className="codeHash">HASH</CodeComponent> function however you want, you never want to add
                        time complexity here.
                    </li>
                </ul>
            </ul>
        </CalloutComponent>
        <AccordionComponent>
            <InsertTimeComplexity/>
            <InsertUnhappyTimeComplexity/>
            <RemoveTimeComplexity/>
            <RemoveUnhappyTimeComplexity/>
            <GetTimeComplexity/>
            <ContainsTimeComplexity/>
        </AccordionComponent>
    </H2SectionComponent>)
}

function InsertTimeComplexity() {
    const [activeIndex, setActiveIndex] = React.useState(0)
    return (
        <AccordionItemComponent header={<AccordionHeaderTextExtraTimeComplexity title="Insert" timeComplexity="O(1)"
                                                                                extraTimeComplexity="O(n)"/>}>

            <CarouselComponent onIndexChange={(index) => setActiveIndex(index)}>
                <CarouselItemComponent><HashSetInsert87/></CarouselItemComponent>
                <CarouselItemComponent><HashSetInsert87Two/></CarouselItemComponent>
                <CarouselItemComponent><HashSetInsert9/></CarouselItemComponent>
                <CarouselItemComponent><HashSetInsertNeg96/></CarouselItemComponent>
                <CarouselItemComponent><HashSetInsert85/></CarouselItemComponent>
                <CarouselItemComponent><HashSetInsert1/></CarouselItemComponent>
                <CarouselItemComponent><HashSetInsert17/></CarouselItemComponent>
            </CarouselComponent>

            {activeIndex === 0 && <HashSetInsert87FullCaption/>}
            {activeIndex === 1 && <HashSetInsert87TwoFullCaption/>}
            {activeIndex === 2 && <HashSetInsert9FullCaption/>}
            {activeIndex === 3 && <HashSetInsertNeg96FullCaption/>}
            {activeIndex === 4 && <HashSetInsert85FullCaption/>}
            {activeIndex === 5 && <HashSetInsert1FullCaption/>}
            {activeIndex === 6 && <HashSetInsert17FullCaption/>}
        </AccordionItemComponent>
    )
}

function InsertUnhappyTimeComplexity() {
    return (
        <AccordionItemComponent
            header={<AccordionHeaderTextEmphasized title="Insert*" description="Visualizing the worst case of"
                                                   timeComplexity="O(n)"/>}>
            <p>As we saw above in our insert examples, our <CodeComponent>O(n)</CodeComponent> case happens when we need
                to re-size, and therefore, <CodeComponent className="codeHash">REHASH</CodeComponent> our HashSet.
                Hopefully, you've already picked up on what that looks like, but just to be sure, the following depicts
                the <CodeComponent>O(n)</CodeComponent> edge-case when <i>inserting</i> a <CodeComponent
                    className="codeValue">VALUE</CodeComponent> into our HashSet.</p>
            <HashSetInsertUnhappyPath/>
            <HashSetInsertUnhappyFullCaption/>
        </AccordionItemComponent>
    )
}

function RemoveTimeComplexity() {
    const [activeIndex, setActiveIndex] = React.useState(0)
    return (
        <AccordionItemComponent
            header={<AccordionHeaderTextExtraTimeComplexity title="Remove"
                                                            timeComplexity="O(1)"
                                                            extraTimeComplexity="O(n)"/>}>

            <CarouselComponent onIndexChange={(index) => setActiveIndex(index)}>
                <CarouselItemComponent><HashSetRemoveNeg96/></CarouselItemComponent>
                <CarouselItemComponent><HashSetRemoveNeg7/></CarouselItemComponent>
                <CarouselItemComponent><HashSetRemoveNeg44/></CarouselItemComponent>
                <CarouselItemComponent><HashSetRemove82/></CarouselItemComponent>
                <CarouselItemComponent><HashSetRemove21/></CarouselItemComponent>
            </CarouselComponent>

            {activeIndex === 0 && <HashSetRemoveNeg96FullCaption/>}
            {activeIndex === 1 && <HashSetRemoveNeg7FullCaption/>}
            {activeIndex === 2 && <HashSetRemoveNeg44FullCaption/>}
            {activeIndex === 3 && <HashSetRemove82FullCaption/>}
            {activeIndex === 4 && <HashSetRemove21FullCaption/>}
        </AccordionItemComponent>
    )
}

function RemoveUnhappyTimeComplexity() {
    return (
        <AccordionItemComponent
            header={<AccordionHeaderTextEmphasized title="Remove*" description="Visualizing the worst case of"
                                                   timeComplexity="O(n)"/>}>
            <p>As we saw above in our remove examples, our <CodeComponent>O(n)</CodeComponent> case happens when every
                single <CodeComponent className="codeValue">VALUE</CodeComponent> collides with one another, and
                the <CodeComponent className="codeValue">VALUE</CodeComponent> we are looking for is the very last
                element in that sub-collection. Therefore, we must iterate over every single element to find
                the <CodeComponent className="codeValue">VALUE</CodeComponent> we are looking for.</p>
            <HashSetRemoveUnhappyPath/>
            <HashSetRemoveUnhappyFullCaption/>
        </AccordionItemComponent>
    )
}

function GetTimeComplexity() {
    return (
        <AccordionItemComponent
            header={<AccordionHeaderTextExtraTimeComplexity title="Get" timeComplexity="O(1)"
                                                            extraTimeComplexity="O(n)"/>}>
            <ul>
                <li>Hopefully you are starting to see the pattern here.</li>
                <li>What does the get function do under the hood?</li>
                <ol>
                    <li>Figure out where the <CodeComponent className="codeValue">VALUE</CodeComponent> is we are trying
                        to get. To do this, we must generate a <CodeComponent
                            className="codeHash">HASH</CodeComponent> key. <CodeComponent>O(1)</CodeComponent></li>
                    <li>Find the sub-collection at the <CodeComponent
                        className="codeHash">HASH</CodeComponent> index. <CodeComponent>O(1)</CodeComponent></li>
                    <li>Iterate over the sub-collection to find the <CodeComponent
                        className="codeValue">VALUE</CodeComponent> we want to return.
                    </li>
                    <ul>
                        <li>On <strong>AVERAGE</strong>, with low-collision sets. <CodeComponent>O(1)</CodeComponent>
                        </li>
                        <li>At <strong>WORST</strong>, with high-collisions. <CodeComponent>O(n)</CodeComponent></li>
                    </ul>
                    <li>Return that <CodeComponent
                        className="codeValue">VALUE</CodeComponent>. <CodeComponent>O(1)</CodeComponent></li>
                </ol>
                <li>As you can see, in most cases, it is <CodeComponent>O(1)</CodeComponent>, and so that is recommended
                    when determining time complexity, however, it could be up to <CodeComponent>O(n)</CodeComponent> if
                    all of your <CodeComponent className="codeValue">VALUES</CodeComponent> are located at the
                    same <CodeComponent className="codeHash">HASH</CodeComponent>.
                </li>
            </ul>
        </AccordionItemComponent>
    )
}

function ContainsTimeComplexity() {
    return (
        <AccordionItemComponent
            header={<AccordionHeaderTextTimeComplexityComponent title="Contains" timeComplexity="O(1)"/>}>
            <ul>
                <li>Patterns, patterns, patterns.</li>
                <li>What does the contains function do under the hood?</li>
                <ol>
                    <li>Figure out where the <CodeComponent className="codeValue">VALUE</CodeComponent>would be if it
                        was in the set. To do this, we must generate a <CodeComponent
                            className="codeHash">HASH</CodeComponent> key. <CodeComponent>O(1)</CodeComponent></li>
                    <li>Find the sub-collection at the <CodeComponent
                        className="codeHash">HASH</CodeComponent> index. <CodeComponent>O(1)</CodeComponent></li>
                    <li>Iterate over the sub-collection to find the <CodeComponent
                        className="codeValue">VALUE</CodeComponent> we are looking for exists.
                    </li>
                    <ul>
                        <li>On <strong>AVERAGE</strong>, with low-collision sets. <CodeComponent>O(1)</CodeComponent>
                        </li>
                        <li>At <strong>WORST</strong>, with high-collisions. <CodeComponent>O(n)</CodeComponent></li>
                    </ul>
                    <li>Return true if we found the <CodeComponent
                        className="codeValue">VALUE</CodeComponent>. <CodeComponent>O(1)</CodeComponent></li>
                </ol>
                <li>One last time, we see that in most cases, if HashSets are used the way they are intended - with a
                    great <CodeComponent className="codeHash">HASH</CodeComponent> algorithm to minimize collisions, our
                    time complexity will be <CodeComponent>O(1)</CodeComponent> - which is the recommended time to use
                    in most cases. In worst case scenario, it can be <CodeComponent>O(n)</CodeComponent> however.
                </li>
            </ul>
        </AccordionItemComponent>
    )
}

function ProblemSet() {
    return (<ProblemSetComponent dataStructure={Paths.dataStructures.nested.hashset.name}>
        <BasicProblems/>
        <AdvancedProblems/>
    </ProblemSetComponent>)
}

function BasicProblems() {
    return (<H3SectionComponent header="Basic Problems" removeTopPadding={true}>
        <ul>
            <li><LinkComponent link="https://leetcode.com/problems/contains-duplicate/description/">Given an IntArray,
                determine if the array contains a duplicate</LinkComponent></li>
            <li><LinkComponent link="https://leetcode.com/problems/intersection-of-two-arrays/description/">Intersection
                of two arrays</LinkComponent></li>
        </ul>
    </H3SectionComponent>)
}

function AdvancedProblems() {
    return (<H3SectionComponent header="Advanced Problems">
        <ul>
            <li><LinkComponent link="https://leetcode.com/problems/contains-duplicate-iii/">Contains Duplicate
                III</LinkComponent></li>
        </ul>
    </H3SectionComponent>)
}
